import { MutationS2sMailVendorOrdersArgs } from "@TXQueries";

import { TDateISO } from "@client/interfaces";

export type OpeningHours = {
	from: string;
	to: string;
};

export type Price = {
	amount: string;
	currency: string;
};

export type OrderFulfillment = {
	minutes_to_accept: number;
	minutes_to_pick: number;
};

export type ShippingDetailsVendor = {
	address: string;
	address2: string;
	phonenumber: string;
	email: string;
	name: string;
};

export type ShippingDetailsOrder = {
	courier_ordered: CourierOrdered;
	customer: Customer;
	vendor: Address;
};

export type Customer = {
	id?: number | null;
	firstName?: string;
	lastName?: string;
	billing_address: Address | null;
	shipping_address: Address | null;
	items_per_package_treshold?: number;
};
export type Address = {
	address?: string | null;
	address2?: string | null;
	address_message?: string | null;
	phonenumber?: string | null;
	email?: string | null;
	fullname?: string | null;
	firstname?: string | null;
	lastname?: string | null;
	city?: string | null;
	country?: string | null;
	zipcode?: string | null;
};

export type Courier = {
	name: string;
	phonenumber: number;
	price: Price;
	currency: string;
};
export type CourierOrdered = {
	courier: Courier;
	eta_in_store: TDateISO | null;
	eta_at_client: TDateISO | null;
};

export type MediaGallery = {
	id: number;
	image: string;
};

export type VendorDetails = {
	id: number;
	identifier: string;
	address: Address;
	country_code: string;

	delivery_dates_select_all: boolean;

	pickup_popup: "yes" | "no";
	pickup_popup_last_reply: string;
	show_picking_area: "yes" | "no";
	print_products: "yes" | "no";

	delivery_dates_enabled: boolean;
	is_selfshipment?: boolean;
	show_handshake_dialog?: boolean;
	max_orders_count?: number | null;

	report_emails?: string[];

	/** store times configurations */
	time: {
		/** store opening hours */
		time_acceptance_hours: string;
		/** store's time to accept an order (minutes) */
		time_accept_order: number;
		/** store's time to pick an order (minutes) */
		time_pick_order: number;
		/**
		 * store's time until a pending/ picking order is automatically timedout
		 * - used when store is closed for a long time
		 **/
		time_days_to_cancel: number;
	};

	/** store printer settings */
	printer: {
		printer_bluetooth_serial?: string;
		printer_model?: string;
		print_orientation?: string;
		tape_width?: string;
		tape_length?: string;
		tape_gap?: string;
	};
};

export type SendOrdersMailParams = MutationS2sMailVendorOrdersArgs & {
	status_names: OrderStatuses[];
};

export enum OrderStatuses {
	"Pending" = "Pending",
	"Picking" = "Picking",
	"Packed" = "Packed",
	"Shipped" = "Shipped",
	"Delivered" = "Delivered",
	"Rejected" = "Rejected",
	"Timedout" = "Timedout",
}

type OrderMeta = {
	createdAt: string;
	reference: string;
	magentoSaleId?: string | null;

	inventoryStatus:
		| "notReceived"
		| "partiallyReceived"
		| "received"
		| "shipped"
		| "notShipped";

	cancellationStatus: "none" | "full" | "partial" | string;
};

export type Order_MinimalDetails = {
	id: number;

	order_meta: OrderMeta;

	products: Product[];

	status: OrderStatuses;

	trackingNumber?: string | null;

	nr_of_packages?: number;

	dates: {
		scheduled_date?: TDateISO | null;
		actual_date?: TDateISO | null;
		/** time when an order is received while the store is closed */
		received_while_closed?: {
			readonly at: string;

			readonly OrderExtraDetails: OrderExtraDetails;
		};

		received?: {
			/** time when the order was received by the store's app, if store was open */
			readonly received_at: TDateISO;

			/** time until store can accept the order; defined when pulling data from DB, from received_at */
			readonly can_accept_until: TDateISO;
			/**
			 * vendorDetails.time.time_accept_order when order was received
			 * in case the current store's time_accept_order changes after initial accept */
			readonly timebomb_when_received: number;

			readonly OrderExtraDetails: OrderExtraDetails;
		};

		/** keep track of whenever the status of an order changes */
		changes: {
			accepted?: {
				/** time when the store accepted the order */
				readonly accepted_at: TDateISO;
				/** time until store can pick the products; defined when order is accepted, from can_accept_until */
				readonly can_pick_until: TDateISO;
				/**
				 * vendorDetails.time.time_pick_order when order was accepted
				 * in case the current store's time_pick_order changes after initial accept */
				readonly timebomb_when_accepted: number;

				readonly OrderExtraDetails: OrderExtraDetails;
			};

			/** rejection/ timeout */
			cancelled?: {
				timedout?: {
					readonly at: TDateISO;
					readonly OrderExtraDetails: OrderExtraDetails;
					readonly timeout_info_extras: {
						[key: string]: string | number | undefined;
					};
				};
				rejected_at?: TDateISO;
				// when an order is rejected, a rejection reason is supplied
				cancellation_reason?: string;
			};

			picking_done_at?: TDateISO;
			shipped_at?: TDateISO;
			received_by_customer_at?: TDateISO;
		};

		/** last update status and extra */
		last_update?: {
			readonly last_status: OrderStatuses;

			readonly OrderExtraDetails: OrderExtraDetails;
		};

		/** purchase date from magento */
		mage_createdAt?: string;
		cancel_timeout?: number;
	};
};

export type OrderExtraDetails = {
	readonly store_opening_hours: string;
	// string is needed because of graphQL error
	readonly platform: "webapp" | "server" | string;
};

export type Order = Order_MinimalDetails & {
	shipping_details: {
		user_details: Customer;
		courier?: CourierOrdered;
	};
};

export type ProductTXDetails = {
	sku: string;
	url: string;
	image: string;
	media_gallery: MediaGallery[];
	name: string;
	brand: string;
};

export type Product = {
	id?: number;
	sku: string;
	order_details: {
		saleItemId?: number;
		barcode?: string | null;
		size?: string | number | null;
		color?: string;
		pickingarea?: string;
		price: Price;
		quantity: number;
		qtyCanceled?: number;
	};
	tx_details: ProductTXDetails;
	canceled?: boolean;
};

export const OrdersHistoryStatuses: {
	id: number;
	label: string;
	status: OrderStatuses;
}[] = [
	{ id: 0, label: "Packed", status: OrderStatuses.Packed },
	{ id: 1, label: "Delivered", status: OrderStatuses.Shipped },
	{ id: 2, label: "Timed out", status: OrderStatuses.Timedout },
	{ id: 3, label: "Rejected", status: OrderStatuses.Rejected },
];

export const OrdersHistoryTimeframe = [
	{ id: 0, label: "Last 24 Hours" },
	{ id: 1, label: "Last 3 Days" },
	{ id: 2, label: "Past Week" },
	{ id: 3, label: "Past Month" },
	{ id: 4, label: "Last 3 Months" },
];
